/**
 * FEAT-031 / S-213 — Platform-operator analytics dashboard.
 *
 * Cross-tenant system totals + per-tenant table. Client-side guard mirrors the
 * server 403 (usePlatformAdmin → redirect) to avoid a flash of unauthorized
 * content; GET /admin/analytics is the real enforcement. No piece titles, user
 * emails, or key values are ever fetched here.
 */

import { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { useApiQuery } from "@/lib/api";
import { usePlatformAdmin } from "../components/AuthProvider";
import { AnalyticsTable } from "./AnalyticsView";

interface AdminResponse {
  period: { from: string; to: string };
  system: {
    total_tenants: number;
    active_tenants: number;
    total_pieces: number;
    total_input_tokens: number;
    total_output_tokens: number;
    platform_input_tokens: number;
    platform_output_tokens: number;
    byoapi_input_tokens: number;
    byoapi_output_tokens: number;
    // BUG-087 — the operator's own subscription, billed to nobody.
    subscription_input_tokens: number;
    subscription_output_tokens: number;
  };
  by_tenant: {
    tenant_id: number;
    tenant_name: string;
    byoapi_enabled: boolean;
    pieces_total: number;
    input_tokens: number;
    output_tokens: number;
    platform_input_tokens: number;
    byoapi_input_tokens: number;
    subscription_input_tokens: number;
  }[];
}

const RANGES = [
  { key: "30", label: "Last 30 days", days: 30 },
  { key: "90", label: "Last 90 days", days: 90 },
] as const;

const fmt = (n: number) => n.toLocaleString();
const DAY_MS = 24 * 60 * 60 * 1000;

function rangeToParams(days: number): string {
  const to = new Date();
  const from = new Date(to.getTime() - days * DAY_MS);
  return `from=${from.toISOString()}&to=${to.toISOString()}`;
}

export function AdminAnalyticsView() {
  const isPlatformAdmin = usePlatformAdmin();
  const [rangeKey, setRangeKey] = useState<(typeof RANGES)[number]["key"]>("30");
  const days = RANGES.find((r) => r.key === rangeKey)?.days ?? 30;
  const params = useMemo(() => rangeToParams(days), [days]);

  const { data, isLoading, error } = useApiQuery<AdminResponse>(
    ["admin-analytics", rangeKey],
    `/admin/analytics?${params}`,
    { enabled: isPlatformAdmin }
  );

  if (!isPlatformAdmin) return <Navigate to="/kanban" replace />;

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <header className="settings-header">
          <div>
            <h1 className="settings-title">Admin Analytics</h1>
            <p className="settings-sub">Cross-tenant platform usage.</p>
          </div>
          <div className="analytics-range" role="tablist">
            {RANGES.map((r) => (
              <button
                key={r.key}
                type="button"
                className={`view-btn ${rangeKey === r.key ? "is-active" : ""}`}
                onClick={() => setRangeKey(r.key)}
              >
                {r.label}
              </button>
            ))}
          </div>
        </header>

        {isLoading && <div className="panel-empty">Loading…</div>}
        {error && (
          <div className="panel-empty">Could not load admin analytics.</div>
        )}

        {data && !isLoading && (
          <>
            <section className="analytics-stats">
              <div className="stat-card">
                <span className="stat-label">Active tenants</span>
                <span className="stat-value">
                  {fmt(data.system.active_tenants)} / {fmt(data.system.total_tenants)}
                </span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Total pieces</span>
                <span className="stat-value">{fmt(data.system.total_pieces)}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Input tokens</span>
                <span className="stat-value">{fmt(data.system.total_input_tokens)}</span>
              </div>
              <div className="stat-card">
                <span className="stat-label">Output tokens</span>
                <span className="stat-value">{fmt(data.system.total_output_tokens)}</span>
              </div>
            </section>

            <AnalyticsTable
              title="Token split by key source"
              headers={["Source", "Input", "Output"]}
              rows={[
                [
                  "Platform key",
                  fmt(data.system.platform_input_tokens),
                  fmt(data.system.platform_output_tokens),
                ],
                [
                  "BYOAPI",
                  fmt(data.system.byoapi_input_tokens),
                  fmt(data.system.byoapi_output_tokens),
                ],
                [
                  // BUG-087 — the operator's own subscription, billed to nobody.
                  "Your subscription",
                  fmt(data.system.subscription_input_tokens),
                  fmt(data.system.subscription_output_tokens),
                ],
              ]}
              emptyText="No token usage."
            />

            <AnalyticsTable
              title="By tenant"
              headers={[
                "Tenant",
                "BYOAPI",
                "Pieces",
                "Input",
                "Output",
                "Platform in",
                "BYOAPI in",
                "Subscription in",
              ]}
              rows={data.by_tenant.map((t) => [
                t.tenant_name,
                t.byoapi_enabled ? "Yes" : "No",
                fmt(t.pieces_total),
                fmt(t.input_tokens),
                fmt(t.output_tokens),
                fmt(t.platform_input_tokens),
                fmt(t.byoapi_input_tokens),
                fmt(t.subscription_input_tokens),
              ])}
              emptyText="No tenants."
            />
          </>
        )}
      </div>
    </main>
  );
}
